import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

export interface SupportUser {
  id: number;
  username: string;
}

export interface SupportEnvironment {
  id: number;
  name: string;
  host: string;
}

export interface SupportTicketComment {
  id: number;
  ticketId: number;
  content: string;
  isSticky: boolean;
  createdAt: string;
  user: SupportUser;
}

export interface SupportTicket {
  id: number;
  environmentId: number;
  title: string;
  description: string;
  category: string;
  status: string;
  priority: string;
  isSticky: boolean;
  dueDate: string | null;
  createdAt: string;
  updatedAt: string;
  user: SupportUser;
  assignedUser: SupportUser | null;
  environment: SupportEnvironment;
  comments?: SupportTicketComment[];
}

export interface UpdateSupportTicketInput {
  id: number;
  status?: string;
  priority?: string;
}

export interface AddSupportTicketCommentInput {
  ticketId: number;
  content: string;
}

export interface SupportTicketFilters {
  status?: string;
  priority?: string;
  category?: string;
  username?: string;
}

export interface SupportTicketPagination {
  first?: number;
  after?: number;
}

export interface SupportTicketPageInfo {
  endCursor?: number;
  hasNextPage: boolean;
  total: number;
}

export interface PaginatedSupportTickets {
  tickets: SupportTicket[];
  pageInfo: SupportTicketPageInfo;
}

const SUPPORT_TICKETS_FOR_ENVIRONMENT = gql`
  query SupportTicketsForEnvironment(
    $environmentId: Int
    $filters: SupportTicketFiltersInput
    $pagination: SupportTicketPaginationArgs
  ) {
    supportTicketsForEnvironment(
      environmentId: $environmentId
      filters: $filters
      pagination: $pagination
    ) {
      tickets {
        id
        environmentId
        title
        description
        category
        status
        priority
        isSticky
        dueDate
        createdAt
        updatedAt
        user { id username }
        assignedUser { id username }
        environment { id name host }
      }
      pageInfo {
        endCursor
        hasNextPage
        total
      }
    }
  }
`;

const SUPPORT_TICKET = gql`
  query SupportTicket($id: Int!) {
    supportTicket(id: $id) {
      id
      environmentId
      title
      description
      category
      status
      priority
      isSticky
      dueDate
      createdAt
      updatedAt
      user { id username }
      assignedUser { id username }
      environment { id name host }
      comments {
        id
        ticketId
        content
        isSticky
        createdAt
        user { id username }
      }
    }
  }
`;

const UPDATE_SUPPORT_TICKET = gql`
  mutation UpdateSupportTicket($input: UpdateSupportTicketInput!) {
    updateSupportTicket(input: $input) {
      id
      status
      priority
      updatedAt
    }
  }
`;

const ADD_SUPPORT_TICKET_COMMENT = gql`
  mutation AddSupportTicketComment($input: AddSupportTicketCommentInput!) {
    addSupportTicketComment(input: $input) {
      id
      ticketId
      content
      isSticky
      createdAt
      user { id username }
    }
  }
`;

class SupportService {
  private static instance: SupportService;

  private constructor() {}

  static getInstance(): SupportService {
    if (!SupportService.instance) {
      SupportService.instance = new SupportService();
    }
    return SupportService.instance;
  }

  async getTicketsForEnvironment(
    environmentId?: number,
    filters?: SupportTicketFilters,
    pagination?: SupportTicketPagination,
  ): Promise<PaginatedSupportTickets> {
    const variables: Record<string, any> = {};
    if (environmentId) variables.environmentId = environmentId;
    if (filters) {
      const cleanFilters: Record<string, string> = {};
      if (filters.status) cleanFilters.status = filters.status;
      if (filters.priority) cleanFilters.priority = filters.priority;
      if (filters.category) cleanFilters.category = filters.category;
      if (filters.username) cleanFilters.username = filters.username;
      if (Object.keys(cleanFilters).length > 0) variables.filters = cleanFilters;
    }
    if (pagination) variables.pagination = pagination;
    const { data } = await apolloClient.query({
      query: SUPPORT_TICKETS_FOR_ENVIRONMENT,
      variables,
      fetchPolicy: 'network-only',
    });
    return data.supportTicketsForEnvironment;
  }

  async getTicket(id: number): Promise<SupportTicket> {
    const { data } = await apolloClient.query({
      query: SUPPORT_TICKET,
      variables: { id },
      fetchPolicy: 'network-only',
    });
    return data.supportTicket;
  }

  async updateTicket(input: UpdateSupportTicketInput): Promise<SupportTicket> {
    const { data } = await apolloClient.mutate({
      mutation: UPDATE_SUPPORT_TICKET,
      variables: { input },
    });
    return data.updateSupportTicket;
  }

  async addComment(input: AddSupportTicketCommentInput): Promise<SupportTicketComment> {
    const { data } = await apolloClient.mutate({
      mutation: ADD_SUPPORT_TICKET_COMMENT,
      variables: { input },
    });
    return data.addSupportTicketComment;
  }
}

export const supportService = SupportService.getInstance();
